import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes, FaBus, FaClock } from 'react-icons/fa';
import BookingForm from '../../components/common/BookingForm';
import { companyInfo } from '../../data/tourData';
import './MadhmaheshwarTour.css';

const MadhmaheshwarTour = () => {
  return (
    <>
      <Helmet>
        <title>Madhmaheshwar Tour - NomadTrailz</title>
        <meta name="description" content="Experience the sacred Madhmaheshwar pilgrimage with NomadTrailz. Trek through beautiful Himalayan landscapes to reach the ancient Shiva temple." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1754934085/rchcnfir52qakqcqc1em.jpg" 
              alt="Madhmaheshwar" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Madhmaheshwar Tour
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Sacred pilgrimage to Madhmaheshwar temple through pristine Himalayan landscapes
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Madhmaheshwar is one of the five sacred temples of Panch Kedar, dedicated to Lord Shiva. 
                    This spiritual journey takes you through the beautiful Garhwal Himalayas to reach the 
                    ancient temple where Lord Shiva's navel is worshipped. Experience the divine energy and 
                    breathtaking natural beauty of the region with comfortable accommodations and expert guidance.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Sacred Madhmaheshwar temple darshan</li>
                    <li>Visit to Budha Madhmaheshwar</li>
                    <li>Scenic Himalayan trekking experience</li>
                    <li>Evening prayers and spiritual atmosphere</li>
                    <li>Rishikesh and Devprayag Sangam visit</li>
                    <li>Comfortable accommodations in Ransi</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 0: Depart from Delhi</h4>
                    <ul>
                      <li>Depart from Delhi around 10 PM in luxury tempo traveler/Volvo Bus/Car</li>
                      <li>Refreshments like water bottles and eatables provided at start</li>
                      <li>First major stop at Rudra Shiva for dinner (Own expense)</li>
                      <li>Antakshari, Damsharas, dancing sessions and interactive games</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 1: Rishikesh to Ransi</h4>
                    <ul>
                      <li>Early morning: Arrive in Rishikesh, freshen up</li>
                      <li>Morning: Relax and have breakfast</li>
                      <li>Mid-morning: Visit local sights in Rishikesh and Devprayag Sangam</li>
                      <li>Afternoon: Start journey from Rishikesh to Ransi (5-6 hours drive)</li>
                      <li>Reach Ransi, check into hotel, and rest for the night</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Ransi to Madhmaheshwar Temple</h4>
                    <ul>
                      <li>Early morning: Have breakfast and drive to trek starting point (1.5 hr)</li>
                      <li>Mid-morning: Begin trek to Madhmaheshwar Temple (5-6 hours trek)</li>
                      <li>Afternoon: Reach Madhmaheshwar Temple, explore surroundings</li>
                      <li>Evening: Experience evening prayers and serene atmosphere</li>
                      <li>Overnight: Stay in guesthouses or camps near Madhmaheshwar Temple</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Madhmaheshwar Temple to Ransi</h4>
                    <ul>
                      <li>Morning: Start trek to Budha Madhmaheshwar and spend time</li>
                      <li>After breakfast, start trek back from Madhmaheshwar Temple to Ransi</li>
                      <li>Afternoon: Reach Ransi, relax</li>
                      <li>Evening: Head back to Delhi and reach Delhi by next day morning</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />Rishikesh, Devprayag, Ransi, Madhmaheshwar Temple</li>
                    <li><FaCheck className="check-icon" />Accommodation: Ransi & Madhmaheshwar</li>
                    <li><FaCheck className="check-icon" />4 Meals - Day 1: Dinner, Day 2: Breakfast + Dinner, Day 3: Breakfast</li>
                    <li><FaCheck className="check-icon" />All Transport from Delhi to Delhi</li>
                    <li><FaCheck className="check-icon" />Mineral water in traveller</li>
                    <li><FaCheck className="check-icon" />Professional trekking guide</li>
                    <li><FaCheck className="check-icon" />24/7 customer support</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />Any cost involved due to natural calamity or unpredictable circumstances</li>
                    <li><FaTimes className="times-icon" />Any expense involved other than INCLUSIONS SECTION</li>
                    <li><FaTimes className="times-icon" />Personal expenses and tips</li>
                    <li><FaTimes className="times-icon" />Travel insurance</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 6,500</span>
                    <span className="duration">3 Days / 2 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Madhmaheshwar, Uttarakhand</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: May to October</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 10-15 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Moderate</span>
                    </div>
                  </div>

                  <div className="pricing-options">
                    <h4>Cost Per Person</h4>
                    <div className="price-option">
                      <span className="option">TRIPLE SHARE</span>
                      <span className="amount">₹ 6,500 + 5% GST</span>
                    </div>
                    <div className="price-option">
                      <span className="option">DOUBLE SHARE</span>
                      <span className="amount">₹ 7,000 + 5% GST</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="tour-logistics-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3>Tour Logistics</h3>
                  <div className="logistics-grid">
                    <div className="logistics-item">
                      <FaBus className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Point:</span>
                        <span className="logistics-value">Delhi: Akshardham Exit Gate 1 Metro Station</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaClock className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Time:</span>
                        <span className="logistics-value">10:00 PM</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Madhmaheshwar Sacred Trek"
                  tourPrice="₹ 6,500"
                  tourDuration="3 Days / 2 Nights"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default MadhmaheshwarTour;

